import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';

// =============================================================================
// VideoCompletionRate — quartile completion funnel for video campaigns.
//
// Migrated from BizCharts to Recharts. Owns its own v2 card wrapper now
// (moved out of OverviewTab's inline AntD Card) so the section reads as a
// unified panel consistent with the rest of the Display tab.
//
// Data shape (per v1): array of rows with video_firstquartile, video_midpoint,
// video_thirdquartile, video_complete. Summed across rows into four bars.
// =============================================================================

const LEFT_FILL = '#b2d5ff';
const LEFT_STROKE = '#7dafff';

// Short-form number formatter — "1.2M", "450K" — avoids axis truncation on
// large view counts. Parity with DualAxisAreaChart / SingleAxisBarChart.
const compactAxis = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return v;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return n.toLocaleString();
};

const VideoCompletionRate = ({ chartData }) => {
  const rows = useMemo(() => {
    if (isEmpty(chartData)) return [];
    const complete = chartData.reduce((t, o) => (o.video_complete || 0) + t, 0);
    const first = chartData.reduce((t, o) => (o.video_firstquartile || 0) + t, 0);
    const mid = chartData.reduce((t, o) => (o.video_midpoint || 0) + t, 0);
    const third = chartData.reduce((t, o) => (o.video_thirdquartile || 0) + t, 0);
    return [
      { percentage: '25%', views: first },
      { percentage: '50%', views: mid },
      { percentage: '75%', views: third },
      { percentage: '100%', views: complete },
    ];
  }, [chartData]);

  if (isEmpty(rows)) return null;

  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    // Completion vs first quartile — gives context to the value on hover.
    const firstQ = rows[0] && rows[0].views;
    const rateVsFirst = firstQ > 0 ? ((row.views / firstQ) * 100).toFixed(1) : null;
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #eeeeee',
          borderRadius: 12,
          boxShadow:
            '0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.10)',
          fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
          fontSize: 13,
          padding: 12,
          minWidth: 160,
        }}
      >
        <div
          style={{
            color: '#111827',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            marginBottom: 4,
          }}
        >
          {label} quartile
        </div>
        <div style={{ color: '#374151', fontSize: 12, lineHeight: 1.5 }}>
          Views: <strong>{Number(row.views).toLocaleString()}</strong>
          {rateVsFirst && label !== '25%' && (
            <>
              <br />
              <span style={{ color: '#6b7280', fontSize: 11 }}>
                {rateVsFirst}% of 25% views
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="campaign-overview__video">
      <header className="campaign-overview__video-head">
        <div className="campaign-overview__video-eyebrow mono-eyebrow">
          Video Completion Rate
        </div>
      </header>
      <div className="campaign-overview__video-body">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={rows}
            margin={{ top: 16, right: 24, bottom: 8, left: 0 }}
          >
            <defs>
              <linearGradient id="ds-video-bar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={LEFT_STROKE} stopOpacity={0.9} />
                <stop offset="100%" stopColor={LEFT_FILL} stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
            <XAxis
              dataKey="percentage"
              stroke="#94a3b8"
              fontSize={12}
              tickMargin={8}
              axisLine={{ stroke: '#e8e8e8' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={compactAxis}
              stroke={LEFT_STROKE}
              fontSize={12}
              tickMargin={8}
              axisLine={false}
              tickLine={false}
              width={64}
            />
            <Tooltip content={renderTooltip} cursor={{ fill: 'rgba(203, 213, 225, 0.2)' }} />
            <Bar
              dataKey="views"
              name="Views"
              fill="url(#ds-video-bar)"
              radius={[6, 6, 0, 0]}
              animationDuration={700}
              animationEasing="ease-out"
              isAnimationActive
            >
              {rows.map((_, idx) => (
                <Cell key={`cell-${idx}`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

export default VideoCompletionRate;
